/**
 * Dokument der Marke (0071): PDF prüfen, hochladen und mit Prüfwert anmelden, Unterschriftsseite rendern, Lesen bestätigen.
 * Die Datenbank prüft alles noch einmal (Pfad, Datei, Prüfwert beim Versand, Lesebestätigung vor der Unterschrift); diese
 * Wege sind die der App und testbar gegen die Demo-Datenbank.
 */
import {
  DOCUMENT_COVER_VARIABLES, DOCUMENT_MAX_BYTES, documentCoverTemplate, documentCoverValues, looksLikePdf,
  type ContractContext, type ContractRole, type Currency, type Language, type MoneyLocale,
} from "@talentkontor/domain";
import type { Api } from "../../../data/api";
import { renderDraft } from "./pruefen";
import { safeFileName } from "./daten";

export type DocumentKind = "template" | "brand_pdf";

export interface DocumentFacts {
  name: string;
  size: number;
  sha256: string;
}

/** SHA-256 einer Datei als Hex (WebCrypto, wie der Prüfwert in der Datenbank). */
export async function sha256OfBlob(b: Blob): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", await b.arrayBuffer());
  return Array.from(new Uint8Array(buf), (x) => x.toString(16).padStart(2, "0")).join("");
}

/** Klartext, wenn die Datei kein brauchbares PDF ist (Art, Größe, Dateikopf); sonst null. */
export async function pdfProblem(file: Blob | null): Promise<string | null> {
  if (!file) return "Bitte das PDF der Marke wählen.";
  if (file.size > DOCUMENT_MAX_BYTES) return "Die Datei ist größer als 15 MB.";
  if (file.size < 5) return "Die Datei ist leer.";
  if (file.type && file.type !== "application/pdf") return "Bitte eine PDF-Datei wählen.";
  const head = new Uint8Array(await file.slice(0, 5).arrayBuffer());
  return looksLikePdf(head) ? null : "Die Datei ist kein PDF (der Dateikopf fehlt). Bitte das Original der Marke als PDF hochladen.";
}

/**
 * PDF in den Ordner des Vertrags laden und mit Prüfwert anmelden. Liefert die angemeldeten Angaben (Name bereinigt,
 * Größe laut Speicher).
 */
export async function uploadDocument(api: Api, orgId: string, contractId: string, file: Blob, fileName: string): Promise<DocumentFacts & { path: string }> {
  const problem = await pdfProblem(file);
  if (problem) throw new Error(problem);
  const sha256 = await sha256OfBlob(file);
  const path = `${orgId}/contract/${contractId}/${Date.now()}-${safeFileName(fileName, "dokument.pdf")}`;
  await api.upload("documents", path, file.type === "application/pdf" ? file : new Blob([await file.arrayBuffer()], { type: "application/pdf" }));
  const r = await api.rpc<{ path: string; sha256: string; name: string; size: number }>("contract_set_document", {
    p_contract: contractId, p_path: path, p_sha256: sha256, p_name: fileName, p_size: file.size,
  });
  return { path: r.path, sha256: r.sha256, name: r.name, size: Number(r.size) };
}

/** Unterschriftsseite zu Rollen, Sprache und Datei (Vorlage, Angaben, gerenderter Text). */
export function renderCover(input: {
  language: Language; roles: readonly ContractRole[]; doc: DocumentFacts | null; context: ContractContext; locale: MoneyLocale; currency: Currency;
}) {
  const body = documentCoverTemplate(input.language, input.roles);
  const values = input.doc ? documentCoverValues(input.doc, input.language) : {};
  const r = renderDraft({ body, annex: null, defs: [...DOCUMENT_COVER_VARIABLES], values, context: input.context, locale: input.locale, currency: input.currency });
  return { body, values, html: r.html, unresolved: r.unresolved, defs: [...DOCUMENT_COVER_VARIABLES] };
}

/** Angemeldete Datei eines Vertrags aus seinen Spalten (null, solange keine angemeldet ist). */
export function documentOf(c: { source_pdf_name?: string | null; source_pdf_size?: number | null; source_pdf_sha256?: string | null }): DocumentFacts | null {
  return c.source_pdf_sha256 && c.source_pdf_name ? { name: c.source_pdf_name, size: Number(c.source_pdf_size ?? 0), sha256: c.source_pdf_sha256 } : null;
}

/** Lesen bestätigen: per Link (Marke, Creator ohne Portal, gesetzliche Vertretung). */
export const confirmReadPublic = (api: Api, token: string, sha256: string) =>
  api.rpcAsAnon<{ read_at: string; sha256: string }>("contract_public_confirm_read", { p_token: token, p_sha256: sha256 });
/** Lesen bestätigen im Portal: der Creator für sich, Personen mit Unterschriftsrecht für die Gegenzeichnung. */
export const confirmRead = (api: Api, contractId: string, sha256: string, as: "talent" | "agency") =>
  api.rpc<{ read_at: string; sha256: string }>("contract_confirm_read", { p_contract: contractId, p_sha256: sha256, p_as: as });

/** Klartext zu den Fehlerschlüsseln des Dokuments (Datenbank und Serverfunktion). */
export function documentError(hint: string | undefined, lang: Language = "de"): string | null {
  const en = lang === "en";
  switch (hint) {
    case "document:read_required": return en ? "Please open the document and confirm that you have read it before signing." : "Bitte vor der Unterschrift das Dokument öffnen und bestätigen, dass es gelesen wurde.";
    case "document:hash": case "document:changed": return en ? "The file shown does not match the document that was sent. Please reload the page." : "Die angezeigte Datei stimmt nicht mit dem versendeten Dokument überein. Bitte die Seite neu laden.";
    case "document:missing": return en ? "The brand's PDF is missing." : "Das PDF der Marke fehlt. Bitte hochladen.";
    case "document:cover": return en ? "The signature page does not match the uploaded PDF. Please save the draft again." : "Die Unterschriftsseite passt nicht zum hochgeladenen PDF. Bitte den Entwurf neu speichern.";
    case "document:frozen": return en ? "Since sending, the document can no longer be changed." : "Seit dem Versand ist das Dokument unveränderlich.";
    case "storage": return en ? "The document could not be loaded from storage." : "Das Dokument ließ sich nicht aus dem Speicher laden.";
    default: return null;
  }
}
